/**
 * The chat infobar's FIXED indicator segments — persistent, tooltipped,
 * and clickable, the VSCode status-bar model where a segment both reports
 * and acts (operator 2026-07-30).
 *
 * These are MOVES, not copies. The workspace connector left
 * `SidebarUtility`; its state resolver moved to `lib/gateway-display.ts` so
 * one implementation feeds whoever renders it.
 *
 * ⚠ KNOWN CONSEQUENCE OF THE MOVE, being evaluated live: this bar lives in
 * `ConversationShell`, which only the case workspace and the global chat
 * render — so off a chat surface (`/home`, Contatos, Pendências,
 * Agendamentos, Organização, Configurações) there is now NO connection
 * indicator at all. `ActivityBar` was present everywhere. That is the open
 * question flagged at the component docblock; this build makes it visible
 * rather than theoretical.
 */
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTasklist } from "@/hooks/useTasklist";
import { useScheduleJobs } from "@/hooks/useScheduleJobs";
import { resolveGatewayDisplay } from "@/lib/gateway-display";
import { AgentsIcon, ScheduleIcon, TasksIcon } from "@/lib/monitor-icons";
import { isRightSidebarOnScreen } from "@/lib/right-sidebar-visibility";
import { isCompactViewport, useViewportSize } from "@/hooks/useViewportSize";
import { useGatewayStore } from "@/stores/useGatewayStore";
import { useInfobarStore } from "@/stores/useInfobarStore";
import { useHarnTasksStore, selectTasksByCase } from "@/stores/useHarnTasksStore";
import { useMissionsStore, selectStateCounts } from "@/stores/useMissionsStore";
import { useUIStore } from "@/stores/useUIStore";
import { cn } from "@/lib/utils";

/** Harness-task statuses that mean "work is happening right now". */
const ACTIVE_TASK_STATUSES = new Set(["queued", "running", "starting"]);

/**
 * Pendências — open user tasks, with a BACKLINK to `/tasks`.
 *
 * ⚠ COST, stated rather than buried: `useTasklist` carries its own poll, so
 * mounting it here runs that poll on every chat surface, not just when the
 * Pendências pane is open. It is the existing data path and the operator
 * asked for the counter; but if this bar ever needs to be free, THIS is the
 * segment to gate first, not the counts beside it (those read stores that
 * are already hydrated app-wide and cost nothing extra).
 */
export function PendenciasIndicator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tasks } = useTasklist({ status: ["open"] });
  const count = tasks.length;

  return (
    <Segment
      testid="chat-infobar-pendencias"
      title={t("infobar.pendencias.tooltip", { count })}
      ariaLabel={t("infobar.pendencias.tooltip", { count })}
      onClick={() => navigate("/tasks")}
      data-count={String(count)}
      // Same resting ink as the agents/tasks segment beside it. A non-zero
      // count here is a STANDING FACT ("there are 6 open items"), not
      // activity — brightening it made the two neighbours look like different
      // classes of thing when they are the same class. Emphasis on this bar
      // is reserved for work actually in flight.
      className="text-coffee-500 dark:text-cream-500"
    >
      <TasksIcon size={13} aria-hidden className="shrink-0" />
      {count}
    </Segment>
  );
}

/**
 * Agendamentos — scheduled jobs, with a BACKLINK to `/schedule`.
 *
 * Counts ENABLED jobs: a disabled job is not something the assistant will
 * do, so including it would inflate a number a lawyer reads as "things that
 * will happen". When the gate is not ready the segment renders nothing —
 * "could not look" is not "there are none" (an unknown must never be shown
 * as a zero).
 */
export function AgendamentosIndicator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gate, jobs } = useScheduleJobs();

  if (gate.state !== "ready") return null;
  const count = jobs.filter((j) => j.enabled !== false).length;

  return (
    <Segment
      testid="chat-infobar-agendamentos"
      title={t("infobar.agendamentos.tooltip", { count })}
      ariaLabel={t("infobar.agendamentos.tooltip", { count })}
      onClick={() => navigate("/schedule")}
      data-count={String(count)}
      // Same resting ink as the agents/tasks segment beside it. A non-zero
      // count here is a STANDING FACT ("there are 6 open items"), not
      // activity — brightening it made the two neighbours look like different
      // classes of thing when they are the same class. Emphasis on this bar
      // is reserved for work actually in flight.
      className="text-coffee-500 dark:text-cream-500"
    >
      <ScheduleIcon size={13} aria-hidden className="shrink-0" />
      {count}
    </Segment>
  );
}

/** Shared segment chrome. A segment is a BUTTON: it reports and it acts. */
function Segment({
  testid,
  title,
  ariaLabel,
  onClick,
  children,
  className,
  hoverable = true,
  ...rest
}: {
  testid: string;
  title: string;
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
  /** `false` for segments that own their background — a hover tint on a
   *  filled block either washes the fill out or replaces it, and either way
   *  the colour that ENCODES STATE stops meaning what it says. */
  hoverable?: boolean;
} & Record<`data-${string}`, string | undefined>) {
  return (
    <button
      type="button"
      data-testid={testid}
      title={title}
      aria-label={ariaLabel}
      onClick={onClick}
      className={cn(
        // SQUARE EDGES throughout (operator 2026-07-31) — a status bar is a
        // band of cells, and a rounded chip reads as a floating pill sitting
        // ON the bar rather than part of it.
        // `font-light` restated here, not merely inherited: a <button> resets
        // font-weight to its own default in some UA stylesheets, so the bar's
        // cascade does not reliably reach segment text (the counters rendered
        // visibly heavier than the message beside them).
        "inline-flex h-[20px] shrink-0 items-center gap-1 px-1.5 font-extralight",
        "focus-visible:outline-none",
        hoverable && "transition-colors hover:bg-coffee-900/5 dark:hover:bg-cream-100/10",
        "focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-coffee-500/40 dark:focus-visible:ring-cream-200/40",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

/**
 * The workspace connector. Glyph AND colour carry the merged WS + runtime
 * lifecycle state; the tooltip carries the full sentence.
 */
export function GatewayIndicator() {
  const { t } = useTranslation();
  const connectionState = useGatewayStore((s) => s.connectionState);
  const runtimeState = useGatewayStore((s) => s.runtimeState);
  const repairInProgress = useGatewayStore((s) => s.repairInProgress);
  const gatewayError = useGatewayStore((s) => s.error);

  const d = resolveGatewayDisplay(connectionState, runtimeState, repairInProgress);
  const Icon = d.Icon;
  const message = t(d.msgKey);
  const tooltip = gatewayError ? `${message} (${gatewayError})` : message;

  return (
    <Segment
      testid="chat-infobar-gateway"
      title={tooltip}
      ariaLabel={tooltip}
      // The gateway store exposes no reconnect action, so there is nothing to
      // "do" here — and a segment that swallows clicks is the dead-affordance
      // defect. Instead the click SPEAKS: it posts the full state sentence
      // into the bar's own message area, which is the mechanism this bar
      // already owns and the one surface the operator ruled such messages
      // belong on. Honest on a touch device too, where there is no hover to
      // reveal the tooltip.
      onClick={() =>
        useInfobarStore.getState().post({
          key: "gateway:status",
          text: tooltip,
          mode: connectionState === "connected" ? "success" : "error",
        })
      }
      data-connection-state={connectionState}
      data-runtime-state={runtimeState ?? undefined}
      // A FILLED BLOCK, VSCode-style: full bar height, square edges, flush to
      // the bar's left edge so it reads as part of the chrome rather than a
      // chip floating on it. `-ml-3` cancels the bar's own left padding.
      //
      // NO HOVER STATE AT ALL (operator 2026-07-31). Its background is not
      // decoration — it ENCODES the connection state, so any hover tint makes
      // the block briefly claim a state it is not in. (My first attempt at
      // this used `hover:bg-transparent`, which was worse than a tint: it
      // removed the fill entirely on hover.)
      hoverable={false}
      className={cn("-ml-3 h-[24px] px-2.5 font-extralight", d.colour)}
    >
      <Icon size={13} aria-hidden className={cn("shrink-0", d.spin && "animate-spin motion-reduce:animate-none")} />
      <span className="hidden sm:inline">{t(d.labelKey)}</span>
    </Segment>
  );
}

/**
 * Background agent + task counts. Reads the STORES, not the push stream —
 * the missions/harn pollers are mounted app-wide (the detail dialogs poll
 * unconditionally from `App.tsx`), so these are complete for what they count
 * rather than "whatever arrived since page load".
 *
 * ALWAYS VISIBLE, INCLUDING AT ZERO — the VSCode idiom, where `⊗ 0 ⚠ 0` sits
 * there permanently. My first pass showed RUNNING work only and hid at zero,
 * reasoning that a glance asks "is anything happening"; with nothing running
 * the segment rendered nothing and read as broken. A permanent indicator that
 * disappears is not an indicator. Activity is carried by EMPHASIS (the count
 * brightens and the glyph spins) rather than by presence.
 *
 * ⚠ THESE ARE NOT BYTE-IDENTICAL TO THE SIDEBAR TABS, and the difference is
 * structural, not a bug: the pane's "Tarefas" tab counts standalone harness
 * runs PLUS scheduled runs, and `useScheduledRunsPoll` is the one poll that
 * genuinely lives only inside `TeamOfAgentsPane` — so with the sidebar closed
 * that half is simply not in the SPA. Rather than render a number that
 * silently changes meaning depending on whether a sidebar happens to be open,
 * this counts what it can always see, and the tooltip says exactly what that
 * is. Making the two agree exactly requires hoisting that poll app-level,
 * which the plan currently forbids (I-1) — an operator call, not mine.
 */
export function AgentCountsIndicator() {
  const { t } = useTranslation();
  // ⚠ SELECT NUMBERS, NOT OBJECTS. `selectStateCounts` allocates a fresh
  // object and `selectTasksByCase` a fresh array on every call, so passing
  // either straight to a zustand hook makes the snapshot differ by identity
  // on every render — "The result of getSnapshot should be cached to avoid an
  // infinite loop", then Maximum update depth, then the error boundary eats
  // the whole chat pane. (Cost me exactly that; `TeamOfAgentsPane` avoids it
  // with `useShallow`.) A number is referentially stable, so reducing INSIDE
  // the selector needs no equality helper at all.
  const agentsTotal = useMissionsStore((s) => s.missions.size);
  const tasksTotal = useHarnTasksStore((s) => s.tasks.size);
  // RUNNING MEANS RUNNING (i-quater, 2026-09-12). This used to add `queued`,
  // so a backlog of missions waiting their turn rendered as work in progress —
  // six queued behind a park read as "6 em andamento" while nothing moved.
  const agentsRunning = useMissionsStore((s) => selectStateCounts(s).running);
  // And the one state that actually needs the lawyer was contributing NOTHING,
  // though the store has always exposed it. A parked mission is the single most
  // actionable thing this bar can report, so it gets its own number.
  const agentsAwaiting = useMissionsStore((s) => selectStateCounts(s).awaiting_user);
  const tasksRunning = useHarnTasksStore(
    (s) =>
      selectTasksByCase(s).filter((slot) =>
        ACTIVE_TASK_STATUSES.has(String(slot.summary.status)),
      ).length,
  );
  const viewport = useViewportSize();
  const expanded = useUIStore((s) => s.rightSidebarExpanded);
  const drawerOpen = useUIStore((s) => s.rightDrawerOpen);
  const setDrawerOpen = useUIStore((s) => s.setRightDrawerOpen);
  const setExpanded = useUIStore((s) => s.setRightSidebarExpanded);

  // The action: reveal the surface that OWNS this detail. Uses the shared
  // predicate so the segment opens whichever half of the sidebar the
  // viewport actually uses.
  const onScreen = isRightSidebarOnScreen(viewport, expanded, drawerOpen);
  const reveal = () => {
    if (isCompactViewport(viewport)) setDrawerOpen(!onScreen);
    else setExpanded(!onScreen);
  };

  // ICONS + NUMBERS ONLY, never the words "Agentes"/"Tarefas" (operator
  // 2026-07-30): the glyph carries the category so the bar stays scannable
  // and does not spend its width on labels. The words live in the tooltip,
  // which is also what a screen reader announces.
  const tooltipBase = t("infobar.agentCounts.tooltip", {
    agents: agentsTotal,
    tasks: tasksTotal,
    agentsRunning,
    tasksRunning,
  });
  // Included only when non-zero: a permanent "0 aguardando você" is noise, and
  // noise is what makes an ambient bar stop being read. It LEADS rather than
  // trails, because the base string ends in the call to action ("Abrir o
  // painel.") and a fact parked after the action reads as an afterthought —
  // this is the one state in the segment that actually wants the lawyer.
  const tooltip =
    agentsAwaiting > 0
      ? `${t("infobar.agentCounts.awaitingSuffix", { agentsAwaiting })} ${tooltipBase}`
      : tooltipBase;

  return (
    <Segment
      testid="chat-infobar-agent-counts"
      title={tooltip}
      ariaLabel={tooltip}
      onClick={reveal}
      data-agents={String(agentsTotal)}
      data-tasks={String(tasksTotal)}
      data-agents-running={String(agentsRunning)}
      data-agents-awaiting={String(agentsAwaiting)}
      data-tasks-running={String(tasksRunning)}
    >
      <span
        className={cn(
          "inline-flex items-center gap-1",
          // Emphasis, not presence, marks activity — the segment is always here.
          agentsRunning > 0 ? "text-coffee-900 dark:text-cream-300" : "text-coffee-500 dark:text-cream-500",
        )}
      >
        <AgentsIcon
          size={13}
          aria-hidden
          className={cn("shrink-0", agentsRunning > 0 && "animate-pulse motion-reduce:animate-none")}
        />
        {agentsTotal}
      </span>
      <span
        className={cn(
          "inline-flex items-center gap-1",
          tasksRunning > 0 ? "text-coffee-900 dark:text-cream-300" : "text-coffee-500 dark:text-cream-500",
        )}
      >
        <TasksIcon
          size={13}
          aria-hidden
          className={cn("shrink-0", tasksRunning > 0 && "animate-pulse motion-reduce:animate-none")}
        />
        {tasksTotal}
      </span>
    </Segment>
  );
}
